import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { observer } from "mobx-react"
import {
  Collapse,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
} from "@material-ui/core"
import { ExpandLess, ExpandMore } from "@material-ui/icons"
import { ChargerIcon, HILeft, HIRight } from "images/svg"
import { useLogsStore } from "hooks/stores"
import EargoGenerationService from "../../../services/eargo_generation_service"

const Wrapper = styled.div`
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: 56%;
    transform: translateX(-50%);
    bottom: 0;
    height: 1px;
    width: 78%;
    border-bottom: 1px solid rgba(51, 51, 51, 0.17);
  }

  &:last-child::after {
    content: "";
    width: 0;
    border-bottom: none;
  }

  &:first-child::before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 0;
    height: 1px;
    width: 90%;
    border-bottom: 1px solid rgba(51, 51, 51, 0.17);
  }
`

const ListTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  flex: 0.8;
`

const ListValue = styled.div`
  right: 16px;
  flex: 1;
  word-break: break-all;
  text-align: end;
`

const StyledListItemText = styled(ListItemText)`
  && {
    padding-left: 12px;

    & > span {
      font-size: 14px;
      line-height: 24px;
      font-weight: 800;
    }
  }
`

const StyledListItem = styled(ListItem)`
  && {
    &:first-child {
      margin-top: 8px;
    }

    margin-bottom: 8px;
    padding-top: 0;
    padding-bottom: 0;
    display: flex;
  }
`

const StyledList = styled(List)`
  && {
    padding-left: 38px;
  }
`

const ControlButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  right: 0;
  top: 10px;
`

const ActionWrapper = styled.div`
  z-index: 1401;
  background: white;
  width: 40px;
  height: 40px;
  border-radius: 25px;

  &:hover {
    cursor: pointer;
  }

  &:before {
    content: "…";
    transform: rotate(90deg);
    color: black;
    position: relative;
    font-size: 20px;
    left: calc(50% - 2px);
    display: inline-flex;
  }
`

const StyledMenu = styled(Menu)`
  && {
    margin-top: 40px;
    margin-left: -120px;
    min-width: 100px;
    border-radius: 4px;
    background: transparent;
    color: black;
  }
`

const StyledMenuItem = styled(MenuItem)`
  && {
    z-index: 1500;
  }
`

const deviceIcon = (deviceType) => {
  switch (deviceType) {
    case "charger":
      return <ChargerIcon />
    case "hi_left":
      return <HILeft />
    case "hi_right":
      return <HIRight />
    default:
      return <ChargerIcon />
  }
}

const deviceName = (deviceType) => {
  switch (deviceType) {
    case "charger":
      return ""
    case "hi_left":
      return "(LEFT)"
    case "hi_right":
      return "(RIGHT)"
    default:
      return ""
  }
}

const Device = ({
  device: {
    id,
    serialNumber,
    serialNumberAscii,
    deviceType,
    name,
    firmwareVersion,
    generation,
    systemSerialNumber,
    firmwareDisplayVersion,
  },
}) => {
  const [expand, setExpand] = useState(true)
  const [anchorEl, setAnchorEl] = useState(null)
  const { createResetChargerEvent } = useLogsStore()

  const openDeviceOptions = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExpand = () => {
    setExpand(!expand)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const resetCharger = (deviceId) => {
    createResetChargerEvent(deviceId)
    handleClose()
  }

  return (
    <Wrapper>
      <List component="nav" aria-labelledby="nested-list-subheader">
        <ListItem>
          {deviceIcon(deviceType)}
          <StyledListItemText
            primary={`${
              name || EargoGenerationService.getCurrentGenerationDisplayValue()
            } ${deviceName(deviceType)}`}
          />

          {deviceType === "charger" && (
            <ControlButtons>
              <ActionWrapper id={id} onClick={openDeviceOptions} />
              <StyledMenu
                id={id}
                anchorEl={anchorEl}
                open={Boolean(anchorEl && anchorEl.id === id)}
                onClose={handleClose}
                disablePortal
              >
                {
                  <StyledMenuItem onClick={() => resetCharger(id)}>
                    Reset Charger
                  </StyledMenuItem>
                }
              </StyledMenu>
              {expand ? (
                <ExpandLess button onClick={handleExpand} />
              ) : (
                <ExpandMore button onClick={handleExpand} />
              )}
            </ControlButtons>
          )}
        </ListItem>
        <Collapse in={expand} timeout="auto" unmountOnExit>
          <StyledList component="div" disablePadding>
            {deviceType === "charger" && (
              <StyledListItem>
                <ListTitle>Name</ListTitle>
                <ListValue>{name || "N/A"}</ListValue>
              </StyledListItem>
            )}
            <StyledListItem>
              <ListTitle>Serial number</ListTitle>
              <ListValue>{serialNumberAscii || serialNumber}</ListValue>
            </StyledListItem>
            <StyledListItem>
              <ListTitle>System SN</ListTitle>
              <ListValue>{systemSerialNumber}</ListValue>
            </StyledListItem>
            <StyledListItem>
              <ListTitle>Firmware</ListTitle>
              <ListValue>
                {firmwareDisplayVersion || firmwareVersion || "N/A"}
              </ListValue>
            </StyledListItem>
          </StyledList>
        </Collapse>
      </List>
    </Wrapper>
  )
}

export default observer(Device)
